import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { compact, keyBy } from 'lodash';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
// Utils
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import type {
  TDst,
  TTimeZone,
  TZone,
} from '@timeedit/types/lib/types/timeZone.type';
// TYPES
import { createEmptyTimeZone } from '@timeedit/types/lib/utils/timeZone.util';
import api from '../services/api.service';
// ACTIONS
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from '../utils/sliceHelpers';
import { startAppListening } from './listenerMiddleware';
import type { TAppThunk, TRootState } from './store';

type TTimeZonesResult = IDefaultAPIResult<TTimeZone>;

type TTimeZoneState = IDefaultReducerState & {
  allTimeZones: TTimeZone[];
  timeZoneMap: Record<number, TTimeZone>;
  selectedTimeZones: TTimeZone[];
  zones: TZone[];
  zoneMap: Record<number, TZone>;
  dsts: TDst[];
  dstMap: Record<number, TDst>;
  firstTimeZone: TTimeZone | null;
  secondTimeZone: TTimeZone | null;
  selectedTimeZoneIds: number[];
  fetched: boolean;
};

export const initialState: TTimeZoneState = {
  loading: false,
  hasErrors: false,
  allTimeZones: [],
  selectedTimeZones: [],
  zones: [],
  dsts: [],
  timeZoneMap: {},
  zoneMap: {},
  dstMap: {},
  firstTimeZone: null,
  secondTimeZone: null,
  selectedTimeZoneIds: [],
  fetched: false,
};

// Slice
const slice = createSlice({
  name: 'timeZones',
  initialState,
  reducers: {
    fetchTimeZonesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchTimeZonesSuccess: (
      state,
      { payload }: PayloadAction<TTimeZonesResult>
    ) => {
      state.allTimeZones = payload.results;
      state.timeZoneMap = keyBy(payload.results, 'id');
      state.fetched = true;
      finishedLoadingSuccess(state);
    },
    fetchZonesSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TZone>>
    ) => {
      state.zones = payload.results;
      state.zoneMap = keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchDstsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TDst>>
    ) => {
      state.dsts = payload.results;
      state.dstMap = keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchTimeZonesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createTimeZoneRequest: (state) => {
      isLoadingRequest(state);
    },
    createTimeZoneSuccess: (state, { payload }: PayloadAction<TTimeZone>) => {
      state.allTimeZones.push(payload);
      state.timeZoneMap[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    createTimeZoneFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setSelectedTimeZones: (state, { payload }: PayloadAction<TTimeZone[]>) => {
      state.selectedTimeZones = payload;
    },
    deleteTimeZoneRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteTimeZoneSuccess: (
      state,
      { payload: timeZoneId }: PayloadAction<number>
    ) => {
      state.allTimeZones = state.allTimeZones.filter(
        (timeZone: TTimeZone) => timeZone.id !== timeZoneId
      );
      state.timeZoneMap[timeZoneId] = undefined;
      finishedLoadingSuccess(state);
    },
    deleteTimeZoneFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateTimeZoneRequest: (state) => {
      isLoadingRequest(state);
    },
    updateTimeZoneSuccess: (state, { payload }: PayloadAction<TTimeZone>) => {
      const index = state.allTimeZones.findIndex(
        (timeZone: TTimeZone) => timeZone.id === payload.id
      );
      state.allTimeZones[index] = payload;
      state.timeZoneMap[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    updateTimeZoneFailure: (state) => {
      finishedLoadingFailure(state);
    },
    moveTimeZonesRequest: (state) => {
      isLoadingRequest(state);
    },
    moveTimeZonesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstTimeZone: (state, { payload }: PayloadAction<TTimeZone>) => {
      const prevItem: TTimeZone = state.firstTimeZone;
      state.firstTimeZone = payload;

      if (prevItem?.id === 0) {
        state.timeZoneMap[0] = undefined;
        state.allTimeZones = state.allTimeZones.filter(
          (ar: TTimeZone) => ar.id !== 0
        );
      }
    },
    setSecondTimeZone: (state, { payload }: PayloadAction<TTimeZone>) => {
      state.secondTimeZone = payload;
    },
    createUnsavedTimeZone: (state) => {
      if (state.firstTimeZone?.id === 0) return;

      const newItem: TTimeZone = createEmptyTimeZone();
      state.firstTimeZone = newItem;
      state.timeZoneMap[newItem.id] = newItem;
      state.allTimeZones.unshift(newItem);
    },
    duplicateTimeZone: (state, { payload }: PayloadAction<TTimeZone>) => {
      if (state.firstTimeZone?.id === 0) return;
      const newItem: TTimeZone = {
        ...payload,
        id: 0,
        name: `${payload.name} (copy)`,
      };
      state.firstTimeZone = newItem;
      state.timeZoneMap[newItem.id] = newItem;
      state.allTimeZones.unshift(newItem);
    },
    changeTimeZone: <Key extends keyof TTimeZone>(
      state: TTimeZoneState,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        key: Key;
        value: TTimeZone[Key];
        object: Partial<TTimeZone>;
      }>
    ) => {
      const { isFirstDrawer, key, value, object } = payload;
      const changedObject = object || { [key]: value };
      if (isFirstDrawer) {
        state.firstTimeZone = {
          ...state.firstTimeZone,
          ...changedObject,
        };
      } else {
        state.secondTimeZone = {
          ...state.secondTimeZone,
          ...changedObject,
        };
      }
    },
    discardChanges: (
      state,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        id: number;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstTimeZone = state.timeZoneMap[id];
      } else {
        state.secondTimeZone = state.timeZoneMap[id];
      }
    },
    closeDrawer: (state, { payload: id }: PayloadAction<number>) => {
      if (state.firstTimeZone?.id === id) {
        state.firstTimeZone = null;
      }
      if (state.secondTimeZone?.id === id) {
        state.secondTimeZone = null;
      }
    },
    setSelectedTimezoneIds: (state, { payload }: PayloadAction<number[]>) => {
      state.selectedTimeZoneIds = payload;
    },
  },
});

export default slice.reducer;

// Selectors
export const timeZonesLoading = (state: TRootState) => state.timeZones.loading;
export const timeZonesSelector = (state: TRootState) =>
  state.timeZones.allTimeZones as TTimeZone[];
export const selectedTimeZonesSelector = (state: TRootState) => {
  return state.timeZones.selectedTimeZones;
};
export const zonesSelector = (state: TRootState) => state.timeZones.zones;
export const dstsSelector = (state: TRootState) => state.timeZones.dsts;
export const firstTimeZoneSelector = (state: TRootState) =>
  state.timeZones.firstTimeZone;
export const secondTimeZoneSelector = (state: TRootState) =>
  state.timeZones.secondTimeZone;
export const selectedTimeZoneIdsSelector = (state: TRootState) =>
  state.timeZones.selectedTimeZoneIds;
export const timeZoneMapSelector = (state: TRootState) =>
  state.timeZones.timeZoneMap;
export const zoneMapSelector = (state: TRootState) => state.timeZones.zoneMap;
export const dstMapSelector = (state: TRootState) => state.timeZones.dstMap;
export const timeZoneLoadingSelector = (state: TRootState) =>
  state.timeZones.loading;
export const timeZoneHasErrorsSelector = (state: TRootState) =>
  state.timeZones.hasErrors;
export const timeZonesFetchedSelector = (state: TRootState) =>
  state.timeZones.fetched;

export const defaultTimeZoneSelector = createSelector(
  timeZonesSelector,
  (timeZones) => timeZones.find((tz) => tz.isDefault) || null
);

// Actions
export const {
  fetchTimeZonesRequest,
  fetchTimeZonesSuccess,
  fetchTimeZonesFailure,
  fetchZonesSuccess,
  fetchDstsSuccess,
  createTimeZoneRequest,
  createTimeZoneSuccess,
  createTimeZoneFailure,
  setSelectedTimeZones,
  deleteTimeZoneRequest,
  deleteTimeZoneSuccess,
  deleteTimeZoneFailure,
  updateTimeZoneRequest,
  updateTimeZoneSuccess,
  updateTimeZoneFailure,
  moveTimeZonesRequest,
  moveTimeZonesFailure,
  setFirstTimeZone,
  setSecondTimeZone,
  createUnsavedTimeZone,
  duplicateTimeZone,
  changeTimeZone,
  discardChanges,
  closeDrawer,
  setSelectedTimezoneIds,
} = slice.actions;

export const fetchTimeZones =
  (): TAppThunk<Promise<void>> => async (dispatch) => {
    try {
      dispatch(fetchTimeZonesRequest());
      const timeZones = await api.get({
        endpoint: `/time-zones`,
      });
      dispatch(fetchTimeZonesSuccess(timeZones));
    } catch (e) {
      dispatch(fetchTimeZonesFailure());
      return console.error(e);
    }
  };

export const fetchZones = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchTimeZonesRequest());
    const zones = await api.get({
      endpoint: `/time-zones/zones`,
    });
    dispatch(fetchZonesSuccess(zones));
  } catch (e) {
    dispatch(fetchTimeZonesFailure());
    return console.error(e);
  }
};

export const fetchDsts = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchTimeZonesRequest());
    const dsts = await api.get({
      endpoint: `/time-zones/dsts`,
    });
    dispatch(fetchDstsSuccess(dsts));
  } catch (e) {
    dispatch(fetchTimeZonesFailure());
    return console.error(e);
  }
};

export const saveTimeZone =
  (timeZone: TTimeZone): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(createTimeZoneRequest());
      const { id, isDefault, offset, ...timeZoneBody } = timeZone;
      if (id === 0) {
        await api.post({
          endpoint: `/time-zones`,
          data: timeZoneBody,
        });
        dispatch(fetchTimeZones());
      } else {
        const { timeZone, daylightSavingsTime, ...updateBody } = timeZoneBody;
        const response = await api.patch({
          endpoint: `/time-zones/${id}`,
          data: updateBody,
        });
        dispatch(updateTimeZoneSuccess(response));
      }
      dispatch(closeDrawer(id));
    } catch (e) {
      dispatch(createTimeZoneFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  };

export const setUpdatedTimeZones =
  (timeZones: TTimeZone[]): TAppThunk =>
  async (dispatch) => {
    dispatch(setSelectedTimeZones(timeZones));
  };

export const deleteTimeZone =
  (timeZoneId: number): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteTimeZoneRequest());
      await api.delete({
        endpoint: `/time-zones/${timeZoneId}`,
      });
      dispatch(deleteTimeZoneSuccess(timeZoneId));
      dispatch(closeDrawer(timeZoneId));
    } catch (e) {
      dispatch(deleteTimeZoneFailure());
      return console.error(e);
    }
  };

export const fetchNumberOfReservationsToBeMoved = async (
  fromTimeZone: number,
  toTimeZone: number
) => {
  const amountOfReservationsToBeMoved = await api.get({
    endpoint: `/time-zones/move-reservations?fromTimeZone=${fromTimeZone}&toTimeZone=${toTimeZone}`,
  });
  return amountOfReservationsToBeMoved.numberOfReservationsToBeMoved;
};

export const moveReservationsBetweenTimeZones = async (
  fromTimeZone: number,
  toTimeZone: number
) => {
  const amountOfReservationsMoved = await api.patch({
    endpoint: `/time-zones/move-reservations`,
    data: {
      fromTimeZone,
      toTimeZone,
    },
  });
  return amountOfReservationsMoved.numberOfReservationsMoved;
};

export const checkFirstTimeZoneExists =
  (): TAppThunk<boolean> => (_, getState) =>
    !!getState().timeZones.firstTimeZone;

export const checkTimeZoneSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { timeZones } = getState();
    const { firstTimeZone, secondTimeZone, timeZoneMap } = timeZones;
    if (isFirstDrawer && firstTimeZone && timeZoneMap[firstTimeZone.id]) {
      return checkSavedDisabled(firstTimeZone, timeZoneMap[firstTimeZone.id]);
    }
    if (!isFirstDrawer && secondTimeZone && timeZoneMap[secondTimeZone.id]) {
      return checkSavedDisabled(secondTimeZone, timeZoneMap[secondTimeZone.id]);
    }

    return true;
  };

startAppListening({
  predicate: (_, currentState, prevState) =>
    currentState.timeZones.firstTimeZone?.id !==
      prevState.timeZones.firstTimeZone?.id ||
    currentState.timeZones.secondTimeZone?.id !==
      prevState.timeZones.secondTimeZone?.id,
  effect: (_, { dispatch, getState }) => {
    const {
      timeZones: { firstTimeZone, secondTimeZone },
    } = getState();

    dispatch(
      setSelectedTimezoneIds(compact([firstTimeZone?.id, secondTimeZone?.id]))
    );
  },
});
